import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import "./resumeTemplates/ResumeTemplates.css";
import { TEMPLATES } from "./resumeTemplates";
import {
  buildResumeData,
  buildFileName,
  PAGE_WIDTH_PX,
} from "./resumeTemplates/resumeData";
import { generateResumePdf } from "./resumeTemplates/generatePdf";

const TEMPLATE_COUNT = 6;

const EMPTY_EXPERIENCE = {
  name: "",
  role: "",
  startDate: "",
  endDate: "",
  description: "",
};

const EMPTY_PROJECT = {
  projectName: "",
  projectRole: "",
  technology: "",
  projectLink: "",
  duration: "",
  projectDescription: "",
};

const EDUCATION_SECTIONS = [
  {
    title: "Post Graduation",
    fields: [
      ["pgDegree", "Degree (MCA / MBA / M.Tech)"],
      ["pgCollege", "College / University"],
      ["pgYear", "Year (2024 - 2026)"],
      ["pgScore", "Score / CGPA"],
    ],
  },
  {
    title: "Graduation",
    fields: [
      ["gradDegree", "Degree (B.Tech / BCA / BSc)"],
      ["gradCollege", "College / University"],
      ["gradYear", "Year (2020 - 2024)"],
      ["gradScore", "Score / CGPA"],
    ],
  },
  {
    title: "Intermediate",
    fields: [
      ["interBoard", "Board / Stream"],
      ["interSchool", "School / College Name"],
      ["interYear", "Year (2020)"],
      ["interScore", "Score / Percentage"],
    ],
  },
];

/* ---------------- small presentational pieces ---------------- */

const Field = ({ error, children, span }) => (
  <div
    style={{ ...styles.fieldWrap, gridColumn: span ? "span 2" : undefined }}
    className="cve-field"
  >
    {children}
    {error && (
      <span style={styles.errorInside} className="cve-error-inside">
        {error}
      </span>
    )}
  </div>
);

const SectionCard = ({ title, children, onDelete }) => (
  <div style={styles.card} className="cve-card">
    {(title || onDelete) && (
      <div style={styles.cardHeader}>
        {title ? <h3 style={styles.cardTitle}>{title}</h3> : <span />}
        {onDelete && (
          <button
            onClick={onDelete}
            style={styles.deleteBtn}
            className="cve-small-btn"
            type="button"
          >
            Remove
          </button>
        )}
      </div>
    )}
    {children}
  </div>
);

const AddButton = ({ onClick, label }) => (
  <div style={styles.addRow}>
    <button
      onClick={onClick}
      style={styles.addBtn}
      className="cve-small-btn"
      type="button"
    >
      + {label}
    </button>
  </div>
);

// Renders the chosen template at its real page width, then scales it down to
// fit whatever width is available (phones included). Height is tracked so no
// blank gap is left under the scaled page.
const ScaledPreview = ({ Template, data }) => {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [innerH, setInnerH] = useState(0);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return undefined;

    const update = () => {
      setScale(Math.min(1, wrap.clientWidth / PAGE_WIDTH_PX));
      setInnerH(inner.offsetHeight);
    };
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} style={styles.previewWrap}>
      <div style={{ position: "relative", height: innerH * scale }}>
        <div
          ref={innerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: PAGE_WIDTH_PX,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            background: "#fff",
          }}
        >
          <Template data={data} />
        </div>
      </div>
    </div>
  );
};

/* ---------------- main component ---------------- */

const Resume = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [errors, setErrors] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isFresher, setIsFresher] = useState(false);

  const scrollRef = useRef(null);
  const stepListRef = useRef(null);
  const pdfRef = useRef(null); // full-size, un-scaled copy used only for PDF capture

  const [projectList, setProjectList] = useState([{ ...EMPTY_PROJECT }]);
  const [experienceList, setExperienceList] = useState([
    { ...EMPTY_EXPERIENCE },
  ]);
  const [achievementList, setAchievementList] = useState([{ achievement: "" }]);

  const [formData, setFormData] = useState({
    Name: "",
    position: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    github: "",
    company: "",
    role: "",
    degree: "",
    college: "",
    skills: {
      languages: "",
      frameworks: "",
      cloudDevops: "",
      databases: "",
    },
    summary: "",
    projectName: "",
    projectRole: "",
    technology: "",
    projectLink: "",
    duration: "",
    projectDescription: "",
    pgDegree: "",
    pgCollege: "",
    pgYear: "",
    pgScore: "",
    gradDegree: "",
    gradCollege: "",
    gradYear: "",
    gradScore: "",
    interBoard: "",
    interSchool: "",
    interYear: "",
    interScore: "",
  });

  // One normalized object feeds the live preview AND the PDF.
  const resumeData = useMemo(
    () =>
      buildResumeData({
        formData,
        experienceList,
        projectList,
        achievementList,
        isFresher,
      }),
    [formData, experienceList, projectList, achievementList, isFresher],
  );

  /* ---------------- lifecycle ---------------- */

  // Lock background scroll while the modal is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Scroll the form panel back to top whenever the step changes.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [step]);

  // Keep the active step centred in the horizontal strip on phones.
  useEffect(() => {
    const list = stepListRef.current;
    const el = list?.children[step - 1];
    if (!list || !el) return;
    const left = el.offsetLeft - (list.clientWidth - el.clientWidth) / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [step, isOpen]);

  if (!isOpen) return null;

  const steps = [
    "Personal Info",
    "Work Experience",
    "Education",
    "Skills",
    "Projects",
    "Achievements",
    "Summary",
    "Finalise",
  ];

  /* ---------------- immutable list helpers ---------------- */

  const updateItem = (setter, index, key, value) =>
    setter((list) =>
      list.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );

  const removeItem = (setter, index) =>
    setter((list) => list.filter((_, i) => i !== index));

  /* ---------------- validation ---------------- */

  const getStepErrors = (n) => {
    const newErrors = {};
    const onlyNumbers = /^[0-9\s,]+$/;

    switch (n) {
      case 1: {
        if (!formData.Name.trim()) newErrors.Name = "Name is required";
        else if (/^[0-9\s]+$/.test(formData.Name))
          newErrors.Name = "Enter a valid name";

        if (!formData.position.trim())
          newErrors.position = "Position is required";
        else if (/^[0-9\s]+$/.test(formData.position))
          newErrors.position = "Enter a valid position";

        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/^\S+@\S+\.\S+$/.test(formData.email))
          newErrors.email = "Invalid email";

        if (!formData.phone.trim()) newErrors.phone = "Phone is required";
        else if (!/^[0-9]{10}$/.test(formData.phone))
          newErrors.phone = "Enter a valid 10 digit number";

        if (!formData.location.trim())
          newErrors.location = "Location is required";
        break;
      }

      case 2: {
        if (!isFresher) {
          experienceList.forEach((item, i) => {
            if (!item.name.trim())
              newErrors[`expName${i}`] = "Company required";
            if (!item.role.trim()) newErrors[`expRole${i}`] = "Role required";
            if (!item.startDate)
              newErrors[`expStart${i}`] = "Start date required";
            if (!item.endDate) newErrors[`expEnd${i}`] = "End date required";
            if (!item.description.trim())
              newErrors[`expDesc${i}`] = "Description required";
          });
        }
        break;
      }

      case 3: {
        if (
          !formData.pgDegree &&
          !formData.gradDegree &&
          !formData.interBoard
        ) {
          newErrors.education = "Fill at least one education section";
        }
        break;
      }

      case 4: {
        const s = formData.skills;
        if (!s.languages && !s.frameworks && !s.cloudDevops && !s.databases) {
          newErrors.skills = "Enter at least one skill";
        }
        ["languages", "frameworks", "cloudDevops", "databases"].forEach((k) => {
          if (s[k] && onlyNumbers.test(s[k])) newErrors[k] = "Enter valid skills";
        });
        break;
      }

      case 5: {
        if (
          !projectList.some(
            (p) =>
              p.projectName.trim() &&
              p.projectRole.trim() &&
              p.technology.trim(),
          )
        ) {
          newErrors.projects = "Add at least one valid project";
        }

        projectList.forEach((item, i) => {
          if (item.projectName.trim() && onlyNumbers.test(item.projectName))
            newErrors[`projectName${i}`] = "Enter valid details";
          if (item.projectRole.trim() && onlyNumbers.test(item.projectRole))
            newErrors[`projectRole${i}`] = "Enter valid details";
          if (item.technology.trim() && onlyNumbers.test(item.technology))
            newErrors[`technology${i}`] = "Enter valid details";
        });
        break;
      }

      case 6: {
        achievementList.forEach((item, i) => {
          if (item.achievement.trim() && onlyNumbers.test(item.achievement))
            newErrors[`achievement${i}`] = "Enter a valid achievement";
        });
        break;
      }

      case 7: {
        if (formData.summary.trim() && onlyNumbers.test(formData.summary))
          newErrors.summary = "Enter a valid summary";
        break;
      }

      case 8: {
        if (!selectedTemplate) newErrors.template = "Please select a template";
        break;
      }

      default:
        break;
    }

    return newErrors;
  };

  const validateStep = () => {
    const newErrors = getStepErrors(step);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isStepValidFor = (n) => {
    switch (n) {
      case 1:
        return (
          formData.Name?.trim() &&
          formData.position?.trim() &&
          formData.email?.trim() &&
          formData.phone?.trim() &&
          formData.location?.trim()
        );

      case 2:
        if (isFresher) return true;
        return (
          experienceList.length > 0 &&
          experienceList.every(
            (item) =>
              item.name?.trim() &&
              item.role?.trim() &&
              item.startDate &&
              item.endDate &&
              item.description?.trim(),
          )
        );

      case 3: {
        const isPostGraduationValid =
          formData.pgDegree?.trim() &&
          formData.pgCollege?.trim() &&
          formData.pgYear?.trim() &&
          formData.pgScore?.trim();

        const isGraduationValid =
          formData.gradDegree?.trim() &&
          formData.gradCollege?.trim() &&
          formData.gradYear?.trim() &&
          formData.gradScore?.trim();

        const isIntermediateValid =
          formData.interBoard?.trim() &&
          formData.interSchool?.trim() &&
          formData.interYear?.trim() &&
          formData.interScore?.trim();

        return (
          isPostGraduationValid || isGraduationValid || isIntermediateValid
        );
      }

      case 4:
        return (
          formData.skills.languages?.trim() ||
          formData.skills.frameworks?.trim() ||
          formData.skills.cloudDevops?.trim() ||
          formData.skills.databases?.trim()
        );

      case 5:
        return projectList.some(
          (item) =>
            item.projectName?.trim() &&
            item.projectRole?.trim() &&
            item.technology?.trim(),
        );

      case 6:
      case 7:
        return true;

      case 8:
        return selectedTemplate !== null;

      default:
        return true;
    }
  };

  const isStepValid = () => Boolean(isStepValidFor(step));

  const findFirstInvalidStep = () => {
    for (let n = 1; n <= 8; n += 1) {
      const e = getStepErrors(n);
      if (Object.keys(e).length > 0 || !isStepValidFor(n)) {
        return { step: n, errors: e };
      }
    }
    return null;
  };

  /* ---------------- download (100% in-browser) ---------------- */

  const handleDownload = async () => {
    if (downloading) return;

    const invalid = findFirstInvalidStep();
    if (invalid) {
      setErrors({
        ...invalid.errors,
        download: `Please complete "${steps[invalid.step - 1]}" before downloading.`,
      });
      if (invalid.step !== step) setStep(invalid.step);
      return;
    }

    if (!pdfRef.current) return;

    setDownloading(true);
    setErrors((prev) => ({ ...prev, download: "" }));

    try {
      await generateResumePdf(pdfRef.current, buildFileName(formData.Name));
    } catch (error) {
      console.error("PDF generation failed:", error);
      setErrors((prev) => ({
        ...prev,
        download: "Could not generate the PDF. Please try again.",
      }));
    } finally {
      setDownloading(false);
    }
  };

  const goToStep = (n) => {
    if (n <= maxStepReached) setStep(n);
  };

  const nextStep = () => {
    if (!isStepValid()) return;
    if (!validateStep()) return;
    if (step < 8) {
      const next = step + 1;
      setStep(next);
      setMaxStepReached((m) => Math.max(m, next));
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
    else onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: digits }));
  };

  const handleSkillChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      skills: { ...prev.skills, [name]: value },
    }));
  };

  /* ---------------- step renderers ---------------- */

  const renderFields = () => {
    switch (step) {
      case 1:
        return (
          <div style={styles.grid} className="cve-grid">
            <Field error={errors.Name}>
              <input
                name="Name"
                autoComplete="name"
                placeholder="Name *"
                style={styles.input}
                value={formData.Name}
                onChange={handleChange}
              />
            </Field>

            <Field error={errors.position} span>
              <input
                name="position"
                placeholder="Position * (Frontend Developer, Data Analyst, etc.)"
                style={styles.input}
                value={formData.position}
                onChange={handleChange}
              />
            </Field>

            <Field error={errors.email}>
              <input
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Email *"
                style={styles.input}
                value={formData.email}
                onChange={handleChange}
              />
            </Field>

            <Field error={errors.phone}>
              <input
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                placeholder="Phone (10 digits) *"
                style={styles.input}
                value={formData.phone}
                onChange={handlePhoneChange}
              />
            </Field>

            <Field error={errors.location}>
              <input
                name="location"
                autoComplete="address-level2"
                placeholder="Location *"
                style={styles.input}
                value={formData.location}
                onChange={handleChange}
              />
            </Field>

            <Field>
              <input
                name="linkedin"
                type="url"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="LinkedIn URL"
                style={styles.input}
                value={formData.linkedin}
                onChange={handleChange}
              />
            </Field>

            <Field>
              <input
                name="github"
                type="url"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="GitHub URL"
                style={styles.input}
                value={formData.github}
                onChange={handleChange}
              />
            </Field>
          </div>
        );

      case 2:
        return (
          <div>
            <div style={styles.radioRow} className="cve-radio-row">
              <span style={styles.radioLabel} className="cve-radio-label">
                Experience Type:
              </span>

              <label
                style={{
                  ...styles.radioPill,
                  ...(isFresher ? styles.radioPillActive : {}),
                }}
                className="cve-radio-pill"
              >
                <input
                  type="radio"
                  name="expType"
                  checked={isFresher}
                  onChange={() => {
                    setIsFresher(true);
                    setExperienceList([]);
                    setErrors({});
                  }}
                  style={styles.radioInput}
                />
                Fresher
              </label>

              <label
                style={{
                  ...styles.radioPill,
                  ...(!isFresher ? styles.radioPillActive : {}),
                }}
                className="cve-radio-pill"
              >
                <input
                  type="radio"
                  name="expType"
                  checked={!isFresher}
                  onChange={() => {
                    setIsFresher(false);
                    if (experienceList.length === 0) {
                      setExperienceList([{ ...EMPTY_EXPERIENCE }]);
                    }
                  }}
                  style={styles.radioInput}
                />
                Experienced
              </label>
            </div>

            {!isFresher && (
              <AddButton
                label="Add Company"
                onClick={() =>
                  setExperienceList((list) => [
                    ...list,
                    { ...EMPTY_EXPERIENCE },
                  ])
                }
              />
            )}

            {isFresher ? (
              <div style={styles.emptyState}>
                No work experience to add. You&apos;re all set for this step.
              </div>
            ) : (
              experienceList.map((item, index) => (
                <SectionCard
                  key={index}
                  onDelete={
                    experienceList.length > 1
                      ? () => removeItem(setExperienceList, index)
                      : null
                  }
                >
                  <div style={styles.grid} className="cve-grid">
                    <Field error={errors[`expName${index}`]}>
                      <input
                        placeholder="Company / Organization Name *"
                        style={styles.input}
                        value={item.name}
                        onChange={(e) =>
                          updateItem(
                            setExperienceList,
                            index,
                            "name",
                            e.target.value,
                          )
                        }
                      />
                    </Field>

                    <Field error={errors[`expRole${index}`]}>
                      <input
                        placeholder="Role * (Developer, Manager etc.)"
                        style={styles.input}
                        value={item.role}
                        onChange={(e) =>
                          updateItem(
                            setExperienceList,
                            index,
                            "role",
                            e.target.value,
                          )
                        }
                      />
                    </Field>

                    <Field error={errors[`expStart${index}`]}>
                      <label style={styles.dateLabel}>Start date *</label>
                      <input
                        type="month"
                        style={styles.input}
                        value={item.startDate}
                        onChange={(e) =>
                          updateItem(
                            setExperienceList,
                            index,
                            "startDate",
                            e.target.value,
                          )
                        }
                      />
                    </Field>

                    <Field error={errors[`expEnd${index}`]}>
                      <label style={styles.dateLabel}>End date *</label>
                      <input
                        type="month"
                        style={styles.input}
                        value={item.endDate}
                        onChange={(e) =>
                          updateItem(
                            setExperienceList,
                            index,
                            "endDate",
                            e.target.value,
                          )
                        }
                      />
                    </Field>

                    <Field error={errors[`expDesc${index}`]} span>
                      <textarea
                        placeholder="Description *"
                        style={styles.textarea}
                        value={item.description}
                        onChange={(e) =>
                          updateItem(
                            setExperienceList,
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                      />
                    </Field>
                  </div>
                </SectionCard>
              ))
            )}
          </div>
        );

      case 3:
        return (
          <div>
            {EDUCATION_SECTIONS.map((section) => (
              <SectionCard key={section.title} title={section.title}>
                <div style={styles.grid} className="cve-grid">
                  {section.fields.map(([name, placeholder]) => (
                    <input
                      key={name}
                      name={name}
                      placeholder={placeholder}
                      style={styles.input}
                      value={formData[name]}
                      onChange={handleChange}
                    />
                  ))}
                </div>
              </SectionCard>
            ))}

            {errors.education && <p style={styles.error}>{errors.education}</p>}
          </div>
        );

      case 4:
        return (
          <div style={styles.grid} className="cve-grid">
            <Field error={errors.languages}>
              <input
                name="languages"
                placeholder="Languages (Java, Python, JS)"
                style={styles.input}
                value={formData.skills.languages}
                onChange={handleSkillChange}
              />
            </Field>

            <Field error={errors.frameworks}>
              <input
                name="frameworks"
                placeholder="Frameworks (React, Spring Boot)"
                style={styles.input}
                value={formData.skills.frameworks}
                onChange={handleSkillChange}
              />
            </Field>

            <Field error={errors.cloudDevops}>
              <input
                name="cloudDevops"
                placeholder="Cloud/DevOps (AWS, Docker, CI/CD)"
                style={styles.input}
                value={formData.skills.cloudDevops}
                onChange={handleSkillChange}
              />
            </Field>

            <Field error={errors.databases}>
              <input
                name="databases"
                placeholder="Databases (MySQL, MongoDB)"
                style={styles.input}
                value={formData.skills.databases}
                onChange={handleSkillChange}
              />
            </Field>

            {errors.skills && (
              <p style={{ ...styles.error, gridColumn: "span 2" }}>
                {errors.skills}
              </p>
            )}
          </div>
        );

      case 5:
        return (
          <div>
            <AddButton
              label="Add Project"
              onClick={() =>
                setProjectList((list) => [...list, { ...EMPTY_PROJECT }])
              }
            />

            {projectList.map((item, index) => (
              <SectionCard
                key={index}
                onDelete={
                  projectList.length > 1
                    ? () => removeItem(setProjectList, index)
                    : null
                }
              >
                <div style={styles.grid} className="cve-grid">
                  <Field error={errors[`projectName${index}`]}>
                    <input
                      placeholder="Project Name *"
                      style={styles.input}
                      value={item.projectName}
                      onChange={(e) =>
                        updateItem(
                          setProjectList,
                          index,
                          "projectName",
                          e.target.value,
                        )
                      }
                    />
                  </Field>

                  <Field error={errors[`projectRole${index}`]}>
                    <input
                      placeholder="Your Role *"
                      style={styles.input}
                      value={item.projectRole}
                      onChange={(e) =>
                        updateItem(
                          setProjectList,
                          index,
                          "projectRole",
                          e.target.value,
                        )
                      }
                    />
                  </Field>

                  <Field error={errors[`technology${index}`]}>
                    <input
                      placeholder="Technology Used *"
                      style={styles.input}
                      value={item.technology}
                      onChange={(e) =>
                        updateItem(
                          setProjectList,
                          index,
                          "technology",
                          e.target.value,
                        )
                      }
                    />
                  </Field>

                  <Field>
                    <input
                      type="url"
                      inputMode="url"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="Project Link"
                      style={styles.input}
                      value={item.projectLink}
                      onChange={(e) =>
                        updateItem(
                          setProjectList,
                          index,
                          "projectLink",
                          e.target.value,
                        )
                      }
                    />
                  </Field>

                  <Field span>
                    <input
                      placeholder="Duration (e.g. 6 months, 2 Year)"
                      style={styles.input}
                      value={item.duration}
                      onChange={(e) =>
                        updateItem(
                          setProjectList,
                          index,
                          "duration",
                          e.target.value,
                        )
                      }
                    />
                  </Field>

                  <Field span>
                    <textarea
                      placeholder="Project Description"
                      style={styles.textarea}
                      value={item.projectDescription}
                      onChange={(e) =>
                        updateItem(
                          setProjectList,
                          index,
                          "projectDescription",
                          e.target.value,
                        )
                      }
                    />
                  </Field>
                </div>
              </SectionCard>
            ))}

            {errors.projects && <p style={styles.error}>{errors.projects}</p>}
          </div>
        );

      case 6:
        return (
          <div>
            <AddButton
              label="Add Achievement"
              onClick={() =>
                setAchievementList((list) => [...list, { achievement: "" }])
              }
            />

            {achievementList.map((item, index) => (
              <SectionCard
                key={index}
                onDelete={
                  achievementList.length > 1
                    ? () => removeItem(setAchievementList, index)
                    : null
                }
              >
                <textarea
                  placeholder="Write your achievement..."
                  style={styles.textarea}
                  value={item.achievement}
                  onChange={(e) =>
                    updateItem(
                      setAchievementList,
                      index,
                      "achievement",
                      e.target.value,
                    )
                  }
                />
                {errors[`achievement${index}`] && (
                  <span style={styles.error}>
                    {errors[`achievement${index}`]}
                  </span>
                )}
              </SectionCard>
            ))}
          </div>
        );

      case 7:
        return (
          <Field error={errors.summary}>
            <textarea
              name="summary"
              placeholder="Write professional summary..."
              style={styles.textarea}
              value={formData.summary}
              onChange={handleChange}
            />
          </Field>
        );

      case 8:
        return (
          <div>
            <h3 style={styles.sectionTitle}>Choose Resume Template *</h3>

            <div style={styles.templateGrid} className="cve-template-grid">
              {Array.from({ length: TEMPLATE_COUNT }).map((_, index) => {
                const templateId = index + 1;
                const isSelected = selectedTemplate === templateId;
                return (
                  <button
                    key={templateId}
                    type="button"
                    onClick={() => {
                      setSelectedTemplate(templateId);
                      setErrors((prev) => ({ ...prev, template: "" }));
                    }}
                    style={{
                      ...styles.templateCard,
                      ...(isSelected ? styles.templateCardSelected : {}),
                    }}
                    className="cve-template-card"
                  >
                    <img
                      src={`/ResumeTemplate${templateId}.png`}
                      alt={`Resume template ${templateId}`}
                      style={styles.templateImg}
                      className="cve-template-img"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <p style={styles.templateLabel}>Template {templateId}</p>
                    {isSelected && (
                      <span style={styles.templateCheck}>✓ Selected</span>
                    )}
                  </button>
                );
              })}
            </div>

            {errors.template && <p style={styles.error}>{errors.template}</p>}

            {selectedTemplate && TEMPLATES[selectedTemplate] && (
              <div style={{ marginTop: 28 }}>
                <h3 style={styles.sectionTitle}>Selected Preview</h3>
                <ScaledPreview
                  key={selectedTemplate}
                  Template={TEMPLATES[selectedTemplate]}
                  data={resumeData}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const PdfTemplate = selectedTemplate ? TEMPLATES[selectedTemplate] : null;
  const canContinue = isStepValid();

  return createPortal(
    <div style={styles.overlay} className="cv-elevator-scope cve-overlay">
      <style>{FONT_AND_MOTION_STYLES}</style>

      <div
        style={styles.popup}
        className="cve-popup"
        role="dialog"
        aria-modal="true"
        aria-label="Resume builder"
      >
        <button
          onClick={onClose}
          style={styles.closebtn}
          className="cve-closebtn"
          type="button"
          aria-label="Close"
        >
          ✕
        </button>

        {/* Sidebar (desktop) / header + step strip (mobile) */}
        <div style={styles.sidebar} className="cve-sidebar">
          <div className="cve-sidebar-header">
            <p style={styles.eyebrow} className="cve-eyebrow">
              Case file / Resume Builder
            </p>
            <h2 style={styles.logo} className="cve-logo">
              CV Elevator
            </h2>
            <div style={styles.logoRule} className="cve-logo-rule" />
          </div>

          <div
            style={styles.stepList}
            className="cve-steplist"
            ref={stepListRef}
          >
            {steps.map((item, index) => {
              const n = index + 1;
              const unlocked = n <= maxStepReached;
              return (
                <div
                  key={item}
                  onClick={() => goToStep(n)}
                  role="button"
                  tabIndex={unlocked ? 0 : -1}
                  aria-current={step === n ? "step" : undefined}
                  aria-disabled={!unlocked}
                  onKeyDown={(e) => {
                    if (unlocked && (e.key === "Enter" || e.key === " "))
                      goToStep(n);
                  }}
                  className={`cve-stepitem${step === n ? " is-active" : ""}`}
                  style={{
                    ...styles.stepItem,
                    ...(step === n ? styles.stepItemActive : {}),
                    ...(!unlocked ? styles.stepItemLocked : {}),
                  }}
                >
                  <span style={styles.stepIndex}>
                    {String(n).padStart(2, "0")}
                  </span>
                  <span style={{ flex: 1 }} className="cve-stepitem-label">
                    {item}
                  </span>
                  {n < step && <span style={styles.stepDone}>✓</span>}
                </div>
              );
            })}
          </div>

          <div className="cve-sidebar-footer">
            <div style={styles.progressWrap} className="cve-progress-wrap">
              <div
                style={{ ...styles.progressBar, width: `${(step / 8) * 100}%` }}
              />
            </div>
            <div style={styles.progressText} className="cve-progress-text">
              <span>STEP {String(step).padStart(2, "0")} / 08</span>
              <span>{Math.floor((step / 8) * 100)}%</span>
            </div>
            <div style={styles.stamp} className="cve-stamp">
              DRAFT · CONFIDENTIAL
            </div>
          </div>
        </div>

        {/* Right: title, scrollable form, pinned footer */}
        <div style={styles.content} className="cve-content">
          <div style={styles.topBar} className="cve-topbar">
            <div>
              <h2 style={styles.stepTitle} className="cve-step-title">
                {steps[step - 1]}
              </h2>
              <p style={styles.stepSubtitle} className="cve-step-subtitle">
                Step {step} of 8 · Complete your details
              </p>
            </div>
            <div style={styles.badge} className="cve-badge">
              {String(step).padStart(2, "0")} / 08
            </div>
          </div>

          <div style={styles.scrollArea} className="cve-scroll" ref={scrollRef}>
            <div style={styles.formBox} className="cve-formbox">
              {renderFields()}
            </div>
          </div>

          <div style={styles.footer} className="cve-footer">
            {errors.download && (
              <p style={{ ...styles.error, margin: "0 0 10px" }}>
                {errors.download}
              </p>
            )}

            <div style={styles.btnRow} className="cve-btn-row">
              <button
                style={styles.backBtn}
                className="cve-back-btn"
                onClick={prevStep}
                type="button"
              >
                ← Back
              </button>

              {step < 8 ? (
                <button
                  onClick={nextStep}
                  disabled={!canContinue}
                  type="button"
                  className="cve-next-btn"
                  style={{
                    ...styles.nextBtn,
                    opacity: canContinue ? 1 : 0.4,
                    cursor: canContinue ? "pointer" : "not-allowed",
                  }}
                >
                  Continue →
                </button>
              ) : (
                <button
                  onClick={handleDownload}
                  disabled={!canContinue || downloading}
                  type="button"
                  className="cve-next-btn"
                  style={{
                    ...styles.nextBtn,
                    opacity: canContinue && !downloading ? 1 : 0.4,
                    cursor:
                      canContinue && !downloading ? "pointer" : "not-allowed",
                  }}
                >
                  {downloading ? "Preparing…" : "Download Resume"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Off-screen, un-scaled copy of the selected template: the PDF is
          captured from this node so it matches the preview exactly. */}
      {step === 8 && PdfTemplate && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: -10000,
            top: 0,
            width: PAGE_WIDTH_PX,
            pointerEvents: "none",
          }}
        >
          <div ref={pdfRef}>
            <PdfTemplate data={resumeData} />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default Resume;
/* Colours. Cool greys and one deep green, nothing fancy. */

const PAPER = "#ffffff";
const PAPER_SOFT = "#f4f5f6";
const ACCENT = "#17694a";
const ACCENT_DEEP = "#0f5238";
const ACCENT_TINT = "rgba(23,105,74,0.09)";
const ACCENT_RING = "rgba(23,105,74,0.22)";
const INK = "#16191d";
const INK_MUTED = "#5d646d";
const LINE = "#dfe2e6";
const LINE_STRONG = "#c4c9d0";
const DANGER = "#b42318";
const DANGER_TINT = "#fdf2f1";
const DANGER_LINE = "#f0cdc9";

/* Radii: small things are tight, big containers are rounder. */
const R_FIELD = 6;
const R_CARD = 8;
const R_POPUP = 12;

const FONT_AND_MOTION_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&display=swap');

.cv-elevator-scope, .cv-elevator-scope * { box-sizing: border-box; }

.cv-elevator-scope { font-feature-settings: "tnum" 1; }

.cv-elevator-scope ::selection { background: ${ACCENT_TINT}; color: ${INK}; }

.cv-elevator-scope input::placeholder,
.cv-elevator-scope textarea::placeholder { color: #8c939c; }

.cv-elevator-scope input,
.cv-elevator-scope textarea,
.cv-elevator-scope button { -webkit-tap-highlight-color: transparent; }

/* keyboard focus */
.cv-elevator-scope input:focus-visible,
.cv-elevator-scope textarea:focus-visible,
.cv-elevator-scope button:focus-visible,
.cv-elevator-scope [role="button"]:focus-visible {
  outline: 2px solid ${ACCENT};
  outline-offset: 2px;
}

/* text fields get a ring instead of the outline */
.cv-elevator-scope input:focus,
.cv-elevator-scope textarea:focus {
  outline: none;
  border-color: ${ACCENT} !important;
  box-shadow: 0 0 0 3px ${ACCENT_RING};
}

.cv-elevator-scope input:hover:not(:focus):not([readonly]):not([aria-invalid="true"]),
.cv-elevator-scope textarea:hover:not(:focus):not([aria-invalid="true"]) {
  border-color: ${LINE_STRONG};
}

/* invalid fields: set aria-invalid="true" on the input when it has an error */
.cv-elevator-scope input[aria-invalid="true"],
.cv-elevator-scope textarea[aria-invalid="true"] {
  border-color: ${DANGER} !important;
  background: ${DANGER_TINT} !important;
}
.cv-elevator-scope input[aria-invalid="true"]:focus,
.cv-elevator-scope textarea[aria-invalid="true"]:focus {
  box-shadow: 0 0 0 3px rgba(180,35,24,0.18);
}

.cv-elevator-scope input[readonly] { color: ${INK_MUTED}; cursor: default; }

.cv-elevator-scope *::-webkit-scrollbar { width: 10px; height: 10px; }
.cv-elevator-scope *::-webkit-scrollbar-track { background: transparent; }
.cv-elevator-scope *::-webkit-scrollbar-thumb {
  background: ${LINE_STRONG};
  border-radius: 10px;
  border: 2px solid transparent;
  background-clip: content-box;
}
.cv-elevator-scope *::-webkit-scrollbar-thumb:hover { background: #a9afb8; background-clip: content-box; }

.cv-elevator-scope input[type="month"] { min-width: 0; text-align: left; }

/* hover feedback (inline styles don't do :hover, so it lives here) */
.cve-stepitem:not(.is-active):hover { background: rgba(22,25,29,0.05); color: ${INK}; }

.cve-closebtn:hover { background: ${PAPER_SOFT}; border-color: ${LINE_STRONG}; }

.cve-back-btn:hover { background: ${PAPER_SOFT}; color: ${INK}; border-color: ${LINE_STRONG}; }

.cve-next-btn:not(:disabled):hover,
.cve-small-btn:not(:disabled):hover { filter: brightness(0.92); }

.cve-next-btn:not(:disabled):active,
.cve-small-btn:not(:disabled):active { transform: translateY(1px); }

.cve-next-btn:disabled,
.cve-small-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.cve-radio-pill:hover { border-color: ${LINE_STRONG}; }

/* popup opens with a short fade, since the user just asked for it */
.cve-popup { animation: cve-open .16s ease-out; }
@keyframes cve-open {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .cv-elevator-scope * { transition: none !important; animation: none !important; scroll-behavior: auto !important; }
}

/* ================================================================== */
/*  Phones and tablets (<= 860px)                                      */
/*  The popup becomes a full-screen sheet:                             */
/*    header (logo, progress, step pills)   fixed                      */
/*    step title                            fixed                      */
/*    form                                  scrolls                    */
/*    Back / Continue                       pinned above the home bar  */
/*  !important is needed here because the base styles are inline.      */
/* ================================================================== */

@media (max-width: 860px) {
  .cve-overlay {
    padding: 0 !important;
    align-items: stretch !important;
    background: ${PAPER} !important;
  }

  .cve-popup {
    flex-direction: column !important;
    width: 100% !important;
    max-width: 100% !important;
    height: 100vh !important;
    height: 100dvh !important;
    max-height: none !important;
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
    padding-top: env(safe-area-inset-top, 0px);
    animation: none;
  }

  .cve-closebtn {
    top: calc(env(safe-area-inset-top, 0px) + 8px) !important;
    right: 12px !important;
    width: 40px !important;
    height: 40px !important;
    font-size: 14px !important;
  }

  /* header */
  .cve-sidebar {
    width: 100% !important;
    min-width: 0 !important;
    flex: none !important;
    flex-direction: column !important;
    justify-content: flex-start !important;
    gap: 8px !important;
    padding: 12px 0 10px !important;
    border-right: none !important;
    border-bottom: 1px solid ${LINE} !important;
    overflow: visible !important;
  }

  .cve-sidebar-header { padding: 0 64px 0 16px; }
  .cve-eyebrow, .cve-logo-rule, .cve-stamp, .cve-progress-text { display: none !important; }
  .cve-logo { font-size: 18px !important; margin: 0 !important; line-height: 1.2; }

  .cve-sidebar-footer { order: 1; padding: 0 16px; }
  .cve-progress-wrap { margin-top: 0 !important; }

  /* step pills: swipe sideways, active one is filled */
  .cve-steplist {
    order: 2;
    display: flex !important;
    flex-direction: row !important;
    flex: none !important;
    gap: 6px !important;
    padding: 2px 16px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .cve-steplist::-webkit-scrollbar { display: none; }

  .cve-stepitem {
    flex: none !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 6px !important;
    margin: 0 !important;
    padding: 0 12px !important;
    min-height: 36px;
    border-radius: 999px !important;
    font-size: 13px !important;
    white-space: nowrap;
    scroll-snap-align: center;
    background: ${PAPER_SOFT};
    border: 1px solid ${LINE} !important;
  }
  .cve-stepitem.is-active {
    background: ${ACCENT} !important;
    color: #fff !important;
    border-color: ${ACCENT} !important;
    box-shadow: none !important;
  }
  .cve-stepitem.is-active span { color: #fff !important; }
  .cve-stepitem-label { flex: none !important; }

  /* main pane */
  .cve-content {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    width: 100% !important;
  }

  .cve-topbar { padding: 14px 16px 6px !important; margin: 0 !important; }
  .cve-step-title { font-size: 20px !important; }
  .cve-badge { display: none !important; }

  .cve-scroll {
    padding: 8px 16px 20px !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .cve-formbox {
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
  }

  /* forms */
  .cve-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 14px !important; }
  .cve-grid > * { grid-column: auto !important; }

  .cve-error-inside {
    position: static !important;
    display: block;
    margin-top: 5px;
    font-size: 13px !important;
  }

  .cve-card { padding: 14px !important; margin-bottom: 14px !important; }

  .cve-radio-row { gap: 8px !important; }
  .cve-radio-label { flex: 0 0 100%; margin: 0 !important; }
  .cve-radio-pill { flex: 1; justify-content: center; min-height: 44px; }

  .cve-small-btn { min-height: 40px; padding-left: 16px !important; padding-right: 16px !important; }

  .cve-template-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
  .cve-template-card { padding: 8px !important; }
  .cve-template-img { height: 150px !important; }

  /* pinned footer */
  .cve-footer {
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px)) !important;
  }
  .cve-btn-row { gap: 10px !important; }
  .cve-back-btn { flex: 0 0 auto; min-height: 48px; padding: 0 18px !important; }
  .cve-next-btn { flex: 1 1 auto; min-height: 48px; padding: 0 18px !important; }

  /* 16px stops iOS Safari zooming in on focus */
  .cv-elevator-scope input,
  .cv-elevator-scope textarea { font-size: 16px !important; min-height: 48px; }
  .cv-elevator-scope textarea { min-height: 120px; }
  .cv-elevator-scope input[type="radio"] { min-height: 0; }
}

@media (max-width: 380px) {
  .cve-template-grid { grid-template-columns: minmax(0, 1fr) !important; }
  .cve-template-img { height: 200px !important; }
  .cve-stepitem { padding: 0 10px !important; font-size: 12.5px !important; }
}

/* Short landscape phones: give the form all the height we can */
@media (max-width: 860px) and (max-height: 480px) {
  .cve-sidebar { padding: 6px 0 6px !important; gap: 4px !important; }
  .cve-sidebar-header { display: none; }
  .cve-topbar { padding: 8px 16px 2px !important; }
  .cve-step-subtitle { display: none; }
  .cve-closebtn { top: 4px !important; }
}
`;

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(22,25,29,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: 16,
  },

  popup: {
    width: "100%",
    maxWidth: 1120,
    height: "90vh",
    background: PAPER,
    border: `1px solid ${LINE}`,
    boxShadow: "0 16px 48px rgba(22,25,29,0.18)",
    borderRadius: R_POPUP,
    display: "flex",
    overflow: "hidden",
    position: "relative",
    fontFamily: "'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif",
    color: INK,
    lineHeight: 1.45,
  },

  /* ---------- left column ---------- */

  sidebar: {
    width: 260,
    minWidth: 260,
    background: PAPER_SOFT,
    padding: "26px 18px 22px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRight: `1px solid ${LINE}`,
    overflowY: "auto",
  },

  // plain small text now, no caps / tracking / mono
  eyebrow: {
    fontSize: 13,
    color: INK_MUTED,
    margin: "0 0 4px",
  },

  logo: {
    color: INK,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    margin: 0,
  },

  // a hairline divider instead of a short accent bar
  logoRule: {
    height: 1,
    width: "100%",
    background: LINE,
    margin: "16px 0 14px",
  },

  stepList: {
    flex: 1,
    overflowY: "auto",
  },

  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: R_FIELD,
    marginBottom: 2,
    fontWeight: 500,
    fontSize: 14.5,
    transition: "background .12s ease, color .12s ease",
    border: "1px solid transparent",
    color: INK_MUTED,
    cursor: "pointer",
  },

  stepItemActive: {
    background: PAPER,
    color: INK,
    fontWeight: 600,
    border: `1px solid ${LINE}`,
  },

  stepItemLocked: {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  stepIndex: {
    fontSize: 13,
    fontWeight: 600,
    color: INK_MUTED,
    minWidth: 18,
    fontVariantNumeric: "tabular-nums",
  },

  stepDone: {
    color: ACCENT,
    fontSize: 13,
    fontWeight: 700,
  },

  progressWrap: {
    height: 4,
    background: LINE,
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 16,
  },

  progressBar: {
    height: "100%",
    background: ACCENT,
    transition: "width .25s ease",
  },

  progressText: {
    display: "flex",
    justifyContent: "space-between",
    color: INK_MUTED,
    fontSize: 12.5,
    marginTop: 8,
  },

  // quiet note at the bottom of the sidebar, no dashed box
  stamp: {
    marginTop: 14,
    fontSize: 12.5,
    color: INK_MUTED,
    textAlign: "left",
  },

  /* ---------- right column: fixed title, scrolling body, pinned footer ---------- */

  content: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: PAPER,
  },

  topBar: {
    flex: "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "26px 32px 16px",
  },

  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarGutter: "stable",
    padding: "4px 32px 28px",
  },

  footer: {
    flex: "none",
    padding: "14px 32px",
    borderTop: `1px solid ${LINE}`,
    background: PAPER,
  },

  stepTitle: {
    color: INK,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.015em",
    margin: 0,
  },

  stepSubtitle: {
    color: INK_MUTED,
    fontSize: 14,
    margin: "4px 0 0",
  },

  // small label, not a pill
  badge: {
    background: PAPER_SOFT,
    color: INK_MUTED,
    padding: "5px 10px",
    borderRadius: R_FIELD,
    fontWeight: 600,
    fontSize: 13,
    border: `1px solid ${LINE}`,
    whiteSpace: "nowrap",
    marginRight: 44,
    fontVariantNumeric: "tabular-nums",
  },

  /* ---------- forms ---------- */

  formBox: {
    background: PAPER,
    padding: 22,
    borderRadius: R_CARD,
    border: `1px solid ${LINE}`,
  },

  // extra row gap leaves room for the inline error text under each field
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 20,
    rowGap: 26,
  },

  input: {
    background: PAPER,
    border: `1px solid ${LINE}`,
    padding: "11px 12px",
    borderRadius: R_FIELD,
    color: INK,
    outline: "none",
    transition: "border-color .12s, box-shadow .12s",
    width: "100%",
    fontSize: 15,
    fontFamily: "inherit",
  },

  textarea: {
    background: PAPER,
    border: `1px solid ${LINE}`,
    padding: "11px 12px",
    borderRadius: R_FIELD,
    color: INK,
    outline: "none",
    transition: "border-color .12s, box-shadow .12s",
    width: "100%",
    minHeight: 112,
    resize: "vertical",
    fontSize: 15,
    fontFamily: "inherit",
    lineHeight: 1.55,
  },

  dateLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: INK_MUTED,
    margin: "0 0 5px",
  },

  card: {
    background: PAPER_SOFT,
    border: `1px solid ${LINE}`,
    padding: 18,
    borderRadius: R_CARD,
    marginBottom: 16,
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },

  cardTitle: {
    color: INK,
    fontSize: 15.5,
    fontWeight: 600,
    margin: 0,
  },

  sectionTitle: {
    color: INK,
    marginBottom: 14,
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },

  addRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 16,
  },

  addBtn: {
    background: ACCENT,
    color: "#ffffff",
    border: "none",
    padding: "9px 16px",
    borderRadius: R_FIELD,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    fontFamily: "inherit",
  },

  deleteBtn: {
    background: DANGER_TINT,
    color: DANGER,
    border: `1px solid ${DANGER_LINE}`,
    padding: "6px 12px",
    borderRadius: R_FIELD,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    fontFamily: "inherit",
  },

  btnRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  backBtn: {
    background: "transparent",
    border: `1px solid ${LINE}`,
    color: INK_MUTED,
    padding: "11px 22px",
    borderRadius: R_FIELD,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
    fontFamily: "inherit",
    transition: "background .12s, border-color .12s, color .12s",
  },

  // flat button, no glow
  nextBtn: {
    background: ACCENT,
    border: "none",
    color: "#ffffff",
    padding: "11px 26px",
    borderRadius: R_FIELD,
    fontWeight: 600,
    fontSize: 15,
    fontFamily: "inherit",
    cursor: "pointer",
  },

  closebtn: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: `1px solid ${LINE}`,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: INK_MUTED,
    background: PAPER,
    zIndex: 10,
    transition: "background .12s, border-color .12s",
  },

  error: {
    color: DANGER,
    fontSize: 13.5,
    marginTop: 8,
    fontWeight: 500,
    display: "block",
  },

  fieldWrap: {
    position: "relative",
    width: "100%",
    minWidth: 0,
  },

  errorInside: {
    position: "absolute",
    bottom: -20,
    left: 2,
    fontSize: 12.5,
    color: DANGER,
    fontWeight: 500,
  },

  radioRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 18,
    flexWrap: "wrap",
  },

  radioLabel: {
    color: INK,
    fontWeight: 600,
    fontSize: 14.5,
    marginRight: 6,
  },

  radioPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: R_FIELD,
    border: `1px solid ${LINE}`,
    color: INK_MUTED,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    background: PAPER,
    transition: "border-color .12s, background .12s, color .12s",
  },

  radioPillActive: {
    background: ACCENT_TINT,
    borderColor: ACCENT,
    color: ACCENT_DEEP,
  },

  radioInput: {
    accentColor: ACCENT,
    width: 15,
    height: 15,
    cursor: "pointer",
  },

  emptyState: {
    color: INK_MUTED,
    fontSize: 14.5,
    background: PAPER_SOFT,
    border: `1px dashed ${LINE_STRONG}`,
    borderRadius: R_CARD,
    padding: 22,
    textAlign: "center",
  },

  /* ---------- template picker ---------- */

  templateGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
  },

  templateCard: {
    background: PAPER,
    border: `1px solid ${LINE}`,
    borderRadius: R_CARD,
    padding: 10,
    cursor: "pointer",
    transition: "border-color .12s, box-shadow .12s",
    position: "relative",
    textAlign: "left",
    display: "block",
    minWidth: 0,
  },

  // 1px border + 1px shadow = 2px look without shifting the layout
  templateCardSelected: {
    border: `1px solid ${ACCENT}`,
    boxShadow: `0 0 0 1px ${ACCENT}`,
  },

  templateImg: {
    width: "100%",
    height: 220,
    objectFit: "contain",
    borderRadius: 4,
    background: "#fff",
    padding: 4,
    display: "block",
  },

  templateLabel: {
    color: INK,
    textAlign: "center",
    marginTop: 8,
    fontSize: 14,
    fontWeight: 600,
  },

  templateCheck: {
    position: "absolute",
    top: 8,
    right: 8,
    background: ACCENT,
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 4,
  },

  previewWrap: {
    width: "100%",
    overflow: "hidden",
    border: `1px solid ${LINE}`,
    borderRadius: R_CARD,
    background: PAPER_SOFT,
  },
};